import { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { useMovementTypes, useDeleteMovementType } from '../../hooks/useMovementTypes';
import { FormTitleBox, LoadingSkeleton, ConfirmationDialog, TableBodySkeleton } from '../../common/components';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MovementTypesDataTable from './components/MovementTypesDataTable';
import MovementTypeDialog from './components/MovementTypeDialog';
import { ITEMS_PER_PAGE } from './constants/movementTypesConstants';
import { usePaginatedFilter } from '../../common/hooks/usePaginatedFilter';
import MovementTypesFilters from './components/MovementTypesFilters';

const filterMovementTypes = (collection = [], term = '', filters = {}) => {
  const normalized = term?.toLowerCase().trim();
  return collection.filter((movementType) => {
    const matchesDescription =
      !normalized || movementType.description?.toLowerCase().includes(normalized);

    const matchesStatus =
      !filters.status ||
      filters.status === 'all' ||
      (filters.status === 'active' && movementType.is_active) ||
      (filters.status === 'inactive' && !movementType.is_active);

    const matchesNature =
      !filters.nature ||
      filters.nature === 'all' ||
      movementType.nature === filters.nature;

    return matchesDescription && matchesStatus && matchesNature;
  });
};

const MovementTypes = () => {
  // Estado local del componente
  const [selectedMovementType, setSelectedMovementType] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [movementTypeToDelete, setMovementTypeToDelete] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [filters, setFilters] = useState({ status: 'all', nature: 'all' });

  // Hooks de TanStack Query
  const { data: movementTypes = [], isLoading, error } = useMovementTypes();
  const deleteMovementTypeMutation = useDeleteMovementType();

  const filterFn = useCallback(
    (collection, term) => filterMovementTypes(collection, term, filters),
    [filters]
  );

  const {
    data: paginatedMovementTypes,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    setPage,
  } = usePaginatedFilter({
    data: movementTypes,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn,
  });

  // Handlers
  const handleCreate = () => {
    setSelectedMovementType(null);
    setEditMode(false);
    setIsDialogOpen(true);
  };

  const handleEdit = (movementType) => {
    setSelectedMovementType(movementType);
    setEditMode(true);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (movementType) => {
    setMovementTypeToDelete(movementType);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (movementTypeToDelete) {
      try {
        await deleteMovementTypeMutation.mutateAsync(movementTypeToDelete.uuid);
        setIsDeleteDialogOpen(false);
        setMovementTypeToDelete(null);
      } catch (error) {
        console.error('Error al eliminar tipo de movimiento:', error);
      }
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedMovementType(null);
    setEditMode(false);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setMovementTypeToDelete(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <div className="text-red-600">Error al cargar los tipos de movimiento: {error.message}</div>
      </Box>
    );
  }

  return (
    <>
      <Box>
        <FormTitleBox
          title="Tipos de Movimiento"
          showButton={true}
          buttonText="Nuevo Tipo de Movimiento"
          buttonIcon={AssignmentIcon}
          onButtonClick={handleCreate}
        />

        <MovementTypesFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          description={searchTerm}
          onDescriptionChange={handleSearch}
        />

        <MovementTypesDataTable
          movementTypes={paginatedMovementTypes}
          totalMovementTypes={totalCount}
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
          LoadingComponent={TableBodySkeleton}
        />
      </Box>

      {/* Diálogo de creación/edición */}
      <MovementTypeDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        editMode={editMode}
        selectedMovementType={selectedMovementType}
      />

      {/* Diálogo de confirmación de eliminación */}
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Eliminar Tipo de Movimiento"
        content={`¿Está seguro que desea eliminar el tipo de movimiento "${movementTypeToDelete?.description}"?`}
        isLoading={deleteMovementTypeMutation.isPending}
      />
    </>
  );
};

export default MovementTypes;
